import { mutate, mutateForm } from "./client";
import { API_ENCRYPT } from "./crypto";

const BASE = "/api/orchestrator";
const SUBMIT = `${BASE}/documents/submit`;

/**
 * What the ingestion call answers with.
 *
 * `status` has two values that matter and they mean opposite things for the
 * caller:
 *
 *   queued     (202) the pipeline has the document; poll for the result.
 *   completed  (200) SHA-256 deduplication recognised the file and returned an
 *                    EARLIER extraction. Nothing is running, and polling would
 *                    wait forever for a job that finished before this request.
 *
 * Anything that submits without branching on this eventually reads a cached
 * result as fresh evidence. `force_reprocess` is how you ask for a real run.
 */
export interface SubmitResult {
  status: "queued" | "completed" | string;
  job_id: string;
  document_id: string;
  message?: string;
  /** Present on a dedupe hit; names which store recognised the file. */
  source?: string;
  [key: string]: unknown;
}

export interface SubmitInput {
  tenantId: string;
  documentType: string;
  applicationId?: string;
  /**
   * Stored against the application, not the document. Sending it without an
   * applicationId is rejected by the server (BUSINESS_NAME_WITHOUT_APPLICATION)
   * rather than silently dropped, so the two fields travel together.
   */
  businessName?: string;
  documentId?: string;
  consentGiven: boolean;
  forceReprocess: boolean;
  /** storage-key mode */
  storageProvider?: string;
  storageKey?: string;
  /** upload mode */
  file?: File | null;
}

/** The headers the request carries, kept in one place so curl and fetch agree. */
export function submitHeaders(tenantId: string): Record<string, string> {
  return {
    Authorization: "Bearer <access_token>",
    // Follows the build rather than being hardcoded: with it absent the server
    // ENCRYPTS the reply, and a caller expecting JSON gets {"data":"..."}.
    "x-api-encrypt": API_ENCRYPT ? "true" : "false",
    "X-Tenant-ID": tenantId,
  };
}

/** The JSON body, as sent in storage-key mode. Also what "copy as curl" prints. */
export function submitJsonBody(input: SubmitInput): Record<string, unknown> {
  const body: Record<string, unknown> = {
    storage_provider: input.storageProvider || "azure",
    storage_key: input.storageKey,
    document_type: input.documentType,
    consent_given: input.consentGiven,
  };
  if (input.applicationId) body.application_id = input.applicationId;
  // Guarded on applicationId too: a name typed before the id was filled in
  // would otherwise make the whole submission 400 on a field the user has
  // already moved on from.
  if (input.applicationId && input.businessName?.trim()) {
    body.business_name = input.businessName.trim();
  }
  if (input.documentId) body.document_id = input.documentId;
  if (input.forceReprocess) body.force_reprocess = true;
  return body;
}

/** The tenant a submission is attributed to, as a header the request carries. */
const tenantHeader = (tenantId: string): Record<string, string> =>
  tenantId ? { "X-Tenant-ID": tenantId } : {};

export async function submitDocument(
  input: SubmitInput,
): Promise<SubmitResult> {
  if (input.file) {
    const form = new FormData();
    form.append("file", input.file);
    form.append("document_type", input.documentType);
    form.append("consent_given", String(input.consentGiven));
    if (input.applicationId) form.append("application_id", input.applicationId);
    if (input.applicationId && input.businessName?.trim()) {
      form.append("business_name", input.businessName.trim());
    }
    if (input.documentId) form.append("document_id", input.documentId);
    if (input.forceReprocess) form.append("force_reprocess", "true");
    return mutateForm<SubmitResult>(SUBMIT, form, tenantHeader(input.tenantId));
  }

  return mutate<SubmitResult>(
    SUBMIT,
    "POST",
    submitJsonBody(input),
    tenantHeader(input.tenantId),
  );
}

/**
 * Submit a body exactly as written, for the batch screen.
 *
 * The pasted JSON is already in the endpoint's shape, so it is forwarded
 * untouched rather than mapped through SubmitInput and back. Mapping would
 * silently drop any field this UI does not happen to know about, and the point
 * of pasting an API body is that it is the API body.
 */
export const submitRawDocument = (
  body: Record<string, unknown>,
  tenantId: string,
): Promise<SubmitResult> =>
  mutate<SubmitResult>(SUBMIT, "POST", body, tenantHeader(tenantId));

/** One row of a pasted batch, after parsing but before anything is sent. */
export interface BatchRow {
  index: number;
  body: Record<string, unknown>;
  /** Empty when the row looks submittable. */
  problems: string[];
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Parse and check a pasted batch without sending anything.
 *
 * Only the checks the server answers badly are made here. A bad uuid comes back
 * as a 400 naming no field, and "neither file nor storage_key" as a generic
 * MISSING_DOCUMENT_INPUT -- both worth catching before a run that would fail
 * halfway. Everything the server explains well is left to the server, so this
 * cannot disagree with it.
 */
export function parseBatch(text: string): {
  rows: BatchRow[];
  error: string | null;
} {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return {
      rows: [],
      error: `Not valid JSON: ${e instanceof Error ? e.message : String(e)}`,
    };
  }

  const list = Array.isArray(parsed) ? parsed : [parsed];
  if (list.length === 0) return { rows: [], error: "The array is empty." };

  const rows = list.map((raw, i) => {
    const problems: string[] = [];
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
      return { index: i, body: {}, problems: ["Not a JSON object."] };
    }
    const body = raw as Record<string, unknown>;

    if (!body.storage_key && !body.key_id) {
      problems.push(
        "no storage_key — the UI cannot attach a file to a pasted row",
      );
    }
    for (const field of ["application_id", "document_id"]) {
      const v = body[field];
      if (
        v !== undefined &&
        v !== null &&
        v !== "" &&
        !UUID_RE.test(String(v))
      ) {
        problems.push(`${field} is not a uuid`);
      }
    }
    if (body.business_name && !body.application_id) {
      problems.push(
        "business_name without application_id — the server refuses this with 400",
      );
    }
    if (body.consent_given === false) {
      problems.push(
        "consent_given is false — the server refuses this with 403",
      );
    }
    return { index: i, body, problems };
  });

  return { rows, error: null };
}

/**
 * The status/result read. Takes a job id or a document id -- the route resolves
 * either, which is why the submit response's job_id can be used directly.
 */
export const jobStatusPath = (id: string): string =>
  `${BASE}/documents/${encodeURIComponent(id)}`;

/**
 * The blob behind a storage key, before anything has been submitted.
 *
 * A URL rather than a fetch, because it is handed to an <iframe>, which cannot
 * carry an Authorization header and rides the session cookie instead -- the
 * same way the document viewer already works.
 */
export const storagePreviewPath = (key: string, provider: string): string =>
  `${BASE}/storage/preview?storage_key=${encodeURIComponent(key)}` +
  `&storage_provider=${encodeURIComponent(provider)}`;

/**
 * The same submission as a curl command.
 *
 * The bearer is left as a placeholder rather than filled in. This output exists
 * to be pasted into a ticket or handed to another team, and a screen that
 * helpfully embeds a live 8-hour token in copyable text is how one ends up in a
 * chat log.
 */
export function asCurl(input: SubmitInput, origin: string): string {
  const headers = submitHeaders(input.tenantId);
  const lines = [`curl --location '${origin}${SUBMIT}' \\`];

  for (const [k, v] of Object.entries(headers)) {
    lines.push(`  --header '${k}: ${v}' \\`);
  }

  if (input.file) {
    // No Content-Type: curl sets the multipart boundary itself, for the same
    // reason the browser does.
    lines.push(`  --form 'file=@${input.file.name}' \\`);
    lines.push(`  --form 'document_type=${input.documentType}' \\`);
    lines.push(`  --form 'consent_given=${input.consentGiven}'`);
    return lines.join("\n");
  }

  lines.push(`  --header 'Content-Type: application/json' \\`);
  lines.push(`  --data '${JSON.stringify(submitJsonBody(input), null, 2)}'`);
  return lines.join("\n");
}
